import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconCreditCard,
  IconEdit,
  IconPlus,
  IconShieldLock,
  IconTrash,
} from '@tabler/icons-react';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import {
  useDeletePaymentMethod,
  usePaymentMethods,
} from './paymentMethodsApi';
import { PaymentMethodFormDrawer } from './PaymentMethodFormDrawer';
import { PaymentMethodIcon } from './PaymentMethodIcon';
import type { PaymentMethodDto } from './types';

export function PaymentMethodsPage() {
  const { t } = useTranslation();
  const { data: methods, isLoading } = usePaymentMethods();
  const deleteMutation = useDeletePaymentMethod();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<PaymentMethodDto | null>(null);
  const [deleting, setDeleting] = useState<PaymentMethodDto | null>(null);

  // Always show by sortOrder asc so the table mirrors the cashier tile order.
  const sortedMethods = useMemo(
    () =>
      [...(methods ?? [])].sort((a, b) => {
        if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
        return a.nameAr.localeCompare(b.nameAr);
      }),
    [methods],
  );

  const openCreate = () => {
    setEditing(null);
    setDrawerOpen(true);
  };
  const openEdit = (method: PaymentMethodDto) => {
    setEditing(method);
    setDrawerOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await deleteMutation.mutateAsync(deleting.paymentMethodId);
      notifications.show({
        color: 'green',
        title: t('common.toasts.deleted'),
        message: deleting.nameAr,
      });
      setDeleting(null);
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: apiMessage(err) ?? t('common.toasts.errorBody'),
      });
    }
  };

  const hasMethods = sortedMethods.length > 0;

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.catalog')}
        title={t('paymentMethods.page.title')}
        subtitle={t('paymentMethods.page.subtitle')}
        actions={
          <Button leftSection={<IconPlus size={16} stroke={1.8} />} onClick={openCreate}>
            {t('paymentMethods.page.create')}
          </Button>
        }
      />

      <Alert
        icon={<IconShieldLock size={16} stroke={1.6} />}
        color="gray"
        variant="light"
        radius="sm"
      >
        {t('paymentMethods.recordOnlyNotice')}
      </Alert>

      <Paper p={hasMethods ? 0 : 'lg'}>
        {isLoading ? (
          <Stack align="center" py={48}>
            <Loader size="sm" />
          </Stack>
        ) : !hasMethods ? (
          <EmptyState
            icon={<IconCreditCard size={22} stroke={1.6} />}
            title={t('paymentMethods.empty.title')}
            description={t('paymentMethods.empty.body')}
            actionLabel={t('paymentMethods.page.create')}
            onAction={openCreate}
          />
        ) : (
          <Table verticalSpacing="md" highlightOnHover striped="even">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('paymentMethods.columns.method')}</Table.Th>
                <Table.Th>{t('paymentMethods.columns.code')}</Table.Th>
                <Table.Th>{t('paymentMethods.columns.requiresAmount')}</Table.Th>
                <Table.Th>{t('paymentMethods.columns.sort')}</Table.Th>
                <Table.Th>{t('paymentMethods.columns.status')}</Table.Th>
                <Table.Th style={{ width: 100, textAlign: 'end' }}>
                  {t('common.actions')}
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {sortedMethods.map((method) => (
                <Table.Tr key={method.paymentMethodId}>
                  <Table.Td>
                    <Group gap="sm" wrap="nowrap">
                      <ThemeIcon variant="light" color="wheels" size={34} radius="md">
                        <PaymentMethodIcon code={method.code} />
                      </ThemeIcon>
                      <div>
                        <Text fw={500} c="var(--wheels-ink-strong)">
                          {method.nameAr}
                        </Text>
                        {method.nameEn && (
                          <Text fz="xs" c="var(--wheels-ink-muted)">
                            {method.nameEn}
                          </Text>
                        )}
                      </div>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <span className="font-numeric font-mono text-xs text-[var(--wheels-ink-muted)]">
                      {method.code}
                    </span>
                  </Table.Td>
                  <Table.Td>
                    {method.requiresAmount ? (
                      <Badge color="blue" variant="light" radius="sm">
                        {t('common.yes')}
                      </Badge>
                    ) : (
                      <Badge color="gray" variant="light" radius="sm">
                        {t('common.no')}
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text fz="sm" className="font-numeric">
                      {method.sortOrder}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {method.isActive ? (
                      <Badge color="green" variant="light" radius="sm">
                        {t('common.active')}
                      </Badge>
                    ) : (
                      <Badge color="gray" variant="light" radius="sm">
                        {t('common.inactive')}
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" justify="flex-end" wrap="nowrap">
                      <Tooltip label={t('common.edit')} withinPortal>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          onClick={() => openEdit(method)}
                          aria-label={t('common.edit')}
                        >
                          <IconEdit size={16} stroke={1.6} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={t('common.delete')} withinPortal>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => setDeleting(method)}
                          aria-label={t('common.delete')}
                        >
                          <IconTrash size={16} stroke={1.6} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      <PaymentMethodFormDrawer
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        method={editing}
      />

      <ConfirmDialog
        opened={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        title={t('paymentMethods.delete.title')}
        message={t('paymentMethods.delete.body', { name: deleting?.nameAr ?? '' })}
        loading={deleteMutation.isPending}
      />
    </Stack>
  );
}

function apiMessage(err: unknown): string | null {
  if (typeof err === 'object' && err !== null && 'response' in err) {
    const data = (err as { response?: { data?: { detail?: string; title?: string } } })
      .response?.data;
    return data?.detail ?? data?.title ?? null;
  }
  return null;
}
